import React, { useState, useEffect } from 'react';
import SectionTitle from './SectionTitle';
import { useNavigate } from 'react-router-dom';
import config from '../utils/config';


function getYouTubeId(url) {
  const match = url && url.match(/(?:youtu.be\/|youtube.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return match ? match[1] : null;
}

export default function VideoGallerySection() {
  const [currentVideo, setCurrentVideo] = useState(0);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${config.url}/videos`);
        const data = await res.json();
        setVideos(data.slice(0, 5)); // Show latest 5 videos
      } catch (error) {
        console.error('Error fetching videos:', error);
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  const nextVideo = () => {
    setCurrentVideo((prev) => (prev + 1) % videos.length);
  };
  
  const prevVideo = () => {
    setCurrentVideo((prev) => (prev - 1 + videos.length) % videos.length);
  };

  const goToVideo = (index) => {
    setCurrentVideo(index);
  };

  const handleMainPreviewClick = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">वीडियो लोड हो रहा है...</p>
          </div>
        </div>
      </section>
    );
  }

  if (videos.length === 0) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <div className="bg-white rounded-lg p-8 shadow-lg max-w-md mx-auto">
            <div className="text-6xl mb-4">🎥</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">कोई वीडियो नहीं मिला</h3>
            <p className="text-gray-500">अभी तक कोई वीडियो अपलोड नहीं किया गया है।</p>
          </div>
        </div>
      </section>
    );
  }

  const mainVideo = videos[currentVideo];
  const isYouTube = getYouTubeId(mainVideo.video_url);

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <SectionTitle>वीडियो गैलरी</SectionTitle>
          <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
        </div>

        {/* Main Video Gallery */}
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Navigation */}
            <div className="lg:col-span-1 flex flex-col h-full">
              <div className="bg-white rounded-lg shadow-lg p-6 h-full flex flex-col" style={{ minHeight: '100%', height: '100%' }}>
                <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                  वीडियो सूची
                </h3>
                {/* Navigation Arrows
                <div className="flex justify-center space-x-4 mb-4">
                  <button
                    onClick={prevVideo}
                    className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={nextVideo}
                    className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div> */}
                {/* Video Thumbnails with Preview */}
                <div className="space-y-3 max-h-[32rem] overflow-y-auto flex-1">
                  {videos.map((video, index) => {
                    const isYT = getYouTubeId(video.video_url);
                    return (
                      <div
                        key={video.id}
                        onClick={() => goToVideo(index)}
                        className={`cursor-pointer rounded-lg p-3 transition-all duration-200 flex items-center space-x-3 ${
                          index === currentVideo
                            ? 'bg-orange-50 border-2 border-orange-500 shadow-md'
                            : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                        }`}
                        style={{ minHeight: '5.5rem' }}
                      >
                        <div className="relative w-24 h-16 flex-shrink-0">
                          {isYT ? (
                            <img
                              src={video.thumbnail_url || `https://img.youtube.com/vi/${getYouTubeId(video.video_url)}/0.jpg`}
                              alt={video.title}
                              className="w-full h-full object-cover rounded"
                            />
                          ) : video.video_url && video.video_url.startsWith('/uploads/') ? (
                            <video
                              className="w-full h-full object-cover rounded-lg bg-black"
                              poster={video.thumbnail_url || undefined}
                              muted
                              playsInline
                              preload="metadata"
                            >
                              <source src={video.video_url} type="video/mp4" />
                            </video>
                          ) : (
                            <div className="flex items-center justify-center w-full h-full text-gray-400 bg-gray-200 rounded">No preview</div>
                          )}
                          <div className="absolute inset-0 bg-black bg-opacity-30 rounded flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className={`text-sm font-semibold line-clamp-2 ${
                            index === currentVideo ? 'text-orange-700' : 'text-gray-800'
                          }`}>
                            {video.title}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {video.created_at ? new Date(video.created_at).toLocaleDateString('hi-IN') : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
               {/* See All Button */}
<div className="flex justify-center mt-4">
  <button
    onClick={() => navigate('/videos')}
    className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-6 rounded-full shadow-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2"
  >
    सभी वीडियो देखें
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </button>
</div>

              </div>
            </div>

            {/* Main Video Player/Preview */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                {/* Video Preview Area */}
                <div className="relative cursor-pointer group" onClick={isYouTube || mainVideo.video_url ? handleMainPreviewClick : undefined}>
                  <div className="aspect-w-16 aspect-h-9 bg-gray-900 flex items-center justify-center">
                    {isYouTube ? (
                      <img
                        src={mainVideo.thumbnail_url || `https://img.youtube.com/vi/${getYouTubeId(mainVideo.video_url)}/0.jpg`}
                        alt={mainVideo.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : mainVideo.video_url && mainVideo.video_url.startsWith('/uploads/') ? (
                      <video
                        className="w-full h-full object-cover rounded-lg bg-black"
                        poster={mainVideo.thumbnail_url || undefined}
                        muted
                        playsInline
                        preload="metadata"
                      >
                        <source src={mainVideo.video_url} type="video/mp4" />
                      </video>
                    ) : (
                      <div className="flex items-center justify-center w-full h-full text-gray-400">No video available</div>
                    )}
                    {/* Play Button Overlay */}
                    {(isYouTube || (mainVideo.video_url && mainVideo.video_url.startsWith('/uploads/'))) && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <svg className="w-20 h-20 text-white bg-black bg-opacity-50 rounded-full p-5 group-hover:bg-orange-600 group-hover:text-white transition-colors duration-200" fill="currentColor" viewBox="0 0 84 84"><circle cx="42" cy="42" r="42" opacity="0.5"/><polygon points="33,24 65,42 33,60" fill="currentColor"/></svg>
                      </span>
                    )}
                  </div>
                  {/* Video Navigation Overlay */}
                  <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 hover:opacity-100 transition-opacity">
                    <button
                      onClick={e => { e.stopPropagation(); prevVideo(); }}
                      className="w-12 h-12 rounded-full bg-black bg-opacity-50 text-white flex items-center justify-center hover:bg-opacity-70 transition-all"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); nextVideo(); }}
                      className="w-12 h-12 rounded-full bg-black bg-opacity-50 text-white flex items-center justify-center hover:bg-opacity-70 transition-all"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
                {/* Video Info */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-800">
                      {mainVideo.title}
                    </h3>
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {mainVideo.created_at ? new Date(mainVideo.created_at).toLocaleDateString('hi-IN') : ''}
                    </span>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {mainVideo.description}
                  </p>
                  {/* Video Counter */}
                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      वीडियो {currentVideo + 1} / {videos.length}
                    </div>
                    <div className="flex space-x-2">
                      {videos.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => goToVideo(index)}
                          className={`w-3 h-3 rounded-full transition-colors ${
                            index === currentVideo ? 'bg-orange-500' : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
            </div>
          </div>
          {/* Video Modal */}
          {modalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
              <div className="relative bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 p-4 flex flex-col items-center">
                <button
                  className="absolute top-2 right-2 text-gray-600 hover:text-orange-600 text-2xl font-bold"
                  onClick={closeModal}
                  aria-label="Close"
                >
                  ×
                </button>
                <div className="w-full aspect-video mb-4 bg-black rounded-lg overflow-hidden flex items-center justify-center">
                  {isYouTube ? (
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${getYouTubeId(mainVideo.video_url)}?autoplay=1`}
                      title={mainVideo.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  ) : mainVideo.video_url && mainVideo.video_url.startsWith('/uploads/') ? (
                    <video controls autoPlay className="w-full h-full rounded-lg bg-black">
                      <source src={mainVideo.video_url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : null}
                </div>
                <h2 className="text-xl font-bold mb-1 text-center text-gray-800">{mainVideo.title}</h2>
                {mainVideo.description && (
                  <p className="text-gray-600 text-sm text-center mb-2">{mainVideo.description}</p>
                )}
                {mainVideo.category && (
                  <span className="text-xs bg-orange-100 text-orange-700 rounded px-2 py-1 mt-1 mb-2 font-semibold">
                    {mainVideo.category}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
} 